import { supabase, applyOrgFilter, getActiveOrgOverride, DEFAULT_ORG_ID } from '../supabaseClient'
import { getCurrentStaff } from './auth'

const BUCKET = 'jugadores-documentos'

// Catálogo base minimizado. Todos los documentos son de scope 'club'.
// (Se retiraron acta_nacimiento, curp, ine_tutor, comprobante_domicilio y
//  certificado_medico por el principio de minimización.)
export const BASE_DOCUMENT_TYPES = [
  { value: 'foto', label: 'Foto del jugador' },
  { value: 'carta_responsiva', label: 'Carta responsiva' },
  // Via fisica del consentimiento: el PDF que el tutor firmo a mano.
  { value: 'consentimiento', label: 'Consentimiento firmado' },
  { value: 'reglamento', label: 'Reglamento firmado' },
  { value: 'otro', label: 'Otro' },
]

// Backward-compat: ya no hay documentos de persona.
export const DOC_TYPES = { persona: [], club: BASE_DOCUMENT_TYPES }
export const ALL_DOC_TYPES = BASE_DOCUMENT_TYPES

// Tipos de documento configurados por club (mezcla catálogo base + org_document_types).
// Fallback: si el club no tiene config, todo el catálogo base queda activo.
export const getOrgDocumentTypes = async (orgId = null) => {
  try {
    let query = supabase.from('org_document_types').select('*')
    query = applyOrgFilter(query, orgId)
    const { data, error } = await query
    if (error) throw error

    const config = {}
    ;(data || []).forEach((r) => { config[r.doc_type] = r })

    const types = BASE_DOCUMENT_TYPES.map((t, i) => {
      const c = config[t.value]
      return {
        ...t,
        activo: c ? c.activo : true,
        requerido: c ? c.requerido : false,
        orden: c ? c.orden : i,
      }
    }).sort((a, b) => a.orden - b.orden)

    return { data: types, error: null }
  } catch (error) {
    console.error('[getOrgDocumentTypes]', error)
    return {
      data: BASE_DOCUMENT_TYPES.map((t, i) => ({ ...t, activo: true, requerido: false, orden: i })),
      error,
    }
  }
}

// Activa/desactiva o marca requerido un tipo de documento para el club.
export const setOrgDocumentType = async (docType, { activo, requerido } = {}, orgId = null) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const resolvedOrgId = getActiveOrgOverride() || orgId || staff?.org_id || DEFAULT_ORG_ID

    const patch = { org_id: resolvedOrgId, doc_type: docType, updated_at: new Date().toISOString() }
    if (activo !== undefined) patch.activo = activo
    if (requerido !== undefined) patch.requerido = requerido

    const { data, error } = await supabase
      .from('org_document_types')
      .upsert(patch, { onConflict: 'org_id,doc_type' })
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[setOrgDocumentType]', error)
    return { data: null, error }
  }
}

/**
 * Obtener documentos de un jugador.
 * - scope='persona': docs personales (sin org_id)
 * - scope='club': docs del club actual
 * - scope=null: ambos
 */
export const getPlayerDocuments = async (jugadorId, { scope = null, orgId = null } = {}) => {
  try {
    let query = supabase
      .from('jugadores_documentos')
      .select('*')
      .eq('jugador_id', jugadorId)
      .order('doc_type')

    if (scope) {
      query = query.eq('scope', scope)
    }

    if (orgId && scope === 'club') {
      query = query.eq('org_id', orgId)
    }

    const { data, error } = await query
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getPlayerDocuments]', error)
    return { data: [], error }
  }
}

/**
 * Subir un documento de jugador.
 * Para scope='persona', org_id es null.
 * Para scope='club', org_id es obligatorio.
 */
export const uploadPlayerDocument = async (jugadorId, { scope, docType, file, orgId = null, notes = null }) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    if (!staff) throw new Error('No se pudo obtener el staff actual')

    // Resolver org internamente (override de impersonacion primero); no depender
    // de que el caller lo pase bien. scope 'persona' es global (org_id null).
    if (scope === 'club') {
      orgId = getActiveOrgOverride() || orgId || staff.org_id
      if (!orgId) throw new Error('org_id es obligatorio para documentos de club')
    } else if (scope === 'persona') {
      orgId = null
    }

    // Construir path en storage
    const ext = file.name.split('.').pop() || 'pdf'
    const timestamp = Date.now()
    const storagePath = scope === 'persona'
      ? `personas/${jugadorId}/${docType}_${timestamp}.${ext}`
      : `clubs/${orgId}/${jugadorId}/${docType}_${timestamp}.${ext}`

    // Subir archivo al bucket
    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(storagePath, file, {
        contentType: file.type,
        upsert: false,
      })

    if (uploadError) throw uploadError

    // Insertar registro en tabla (upsert por unique index)
    const record = {
      jugador_id: jugadorId,
      org_id: scope === 'club' ? orgId : null,
      scope,
      doc_type: docType,
      file_path: storagePath,
      file_name: file.name,
      mime_type: file.type,
      size_bytes: file.size,
      uploaded_by: staff.id,
      verified: false,
    }

    // Intentar delete + insert para respetar unique partial indexes
    if (scope === 'persona') {
      await supabase
        .from('jugadores_documentos')
        .delete()
        .eq('jugador_id', jugadorId)
        .eq('doc_type', docType)
        .eq('scope', 'persona')
    } else {
      await supabase
        .from('jugadores_documentos')
        .delete()
        .eq('jugador_id', jugadorId)
        .eq('doc_type', docType)
        .eq('scope', 'club')
        .eq('org_id', orgId)
    }

    const { data, error } = await supabase
      .from('jugadores_documentos')
      .insert(record)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[uploadPlayerDocument]', error)
    return { data: null, error }
  }
}

/**
 * Obtener URL publica (o firmada) de un documento.
 */
export const getDocumentUrl = async (filePath) => {
  try {
    // Bucket privado: URL firmada (respeta RLS y caduca en 5 min)
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(filePath, 300)

    if (error) throw error
    return { url: data?.signedUrl || null, error: null }
  } catch (error) {
    console.error('[getDocumentUrl]', error)
    return { url: null, error }
  }
}

/**
 * Eliminar un documento.
 */
export const deletePlayerDocument = async (docId, filePath) => {
  try {
    // Eliminar archivo de storage
    if (filePath) {
      await supabase.storage.from(BUCKET).remove([filePath])
    }

    // Eliminar registro
    const { error } = await supabase
      .from('jugadores_documentos')
      .delete()
      .eq('id', docId)

    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('[deletePlayerDocument]', error)
    return { error }
  }
}

/**
 * Marcar documento como verificado.
 */
export const verifyPlayerDocument = async (docId, verified = true) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const { data, error } = await supabase
      .from('jugadores_documentos')
      .update({
        verified,
        verified_at: verified ? new Date().toISOString() : null,
        verified_by: verified ? staff?.id : null,
      })
      .eq('id', docId)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[verifyPlayerDocument]', error)
    return { data: null, error }
  }
}

/**
 * Resumen de documentos de un jugador (para checklist).
 * Retorna un mapa { doc_type: { uploaded, verified, doc } }
 */
export const getPlayerDocumentsSummary = async (jugadorId, orgId = null) => {
  try {
    const { data, error } = await getPlayerDocuments(jugadorId)
    if (error) throw error

    const summary = {}

    // Inicializar todos los tipos
    ALL_DOC_TYPES.forEach(({ value }) => {
      summary[value] = { uploaded: false, verified: false, doc: null }
    })

    // Llenar con docs existentes
    ;(data || []).forEach((doc) => {
      // Para docs de club, solo contar los de la org actual
      if (doc.scope === 'club' && orgId && doc.org_id !== orgId) return
      summary[doc.doc_type] = {
        uploaded: true,
        verified: doc.verified,
        doc,
      }
    })

    return { data: summary, error: null }
  } catch (error) {
    console.error('[getPlayerDocumentsSummary]', error)
    return { data: null, error }
  }
}
